/**
 * What one call holds, and how it lets go.
 *
 * Split out of `useHelmetCall` so the state machine there reads as the handshake it is. Releasing is
 * the part that must be right even when everything else has gone wrong, so it lives on its own and
 * is a plain function over the handles rather than a hook — the refs stay declared in the component
 * that uses them, where React's linter can see for itself that they are stable.
 */

export type HelmetCallPhase =
  | { kind: 'idle' }
  /** Asking for the microphone and opening the call. Nothing has reached the helmet yet. */
  | { kind: 'starting' }
  /** The helmet has been told to join. Its side has not answered yet. */
  | { kind: 'ringing' }
  | { kind: 'connected' }
  | { kind: 'ended'; reason: string | null }
  | { kind: 'failed'; reason: string; detail?: string }

/** Keeps the broker's session alive while media flows around it. */
export const PING_MS = 20_000
/** How often to re-offer while the transcoded feed is still starting up. */
export const PLAY_RETRY_MS = 1_500
/** Stop re-offering after this. The broker's own probe gives up around the same time. */
export const PLAY_WINDOW_MS = 60_000
/**
 * How long `call_prepare` may go unanswered before the call is declared failed.
 *
 * Nothing else bounds it. The whole handshake hangs off a broker-synthesised `prepared` ack, and
 * there are at least three ways it never arrives: the broker has no media configuration and drops
 * the command with only a server-side warning; the command is published before this panel's own
 * websocket reaches OPEN and is discarded; or the socket never subscribes at all. In every one the
 * phase stayed `starting` for ever — no dismiss button, because the panel only offers one on a
 * finished call — with the operator's microphone live and every FUTURE emergency blocked behind it.
 */
export const PREPARE_ACK_MS = 15_000
/**
 * How long a lost command channel is tolerated before the call is declared over.
 *
 * The channel really is how the call is ended, so losing it permanently does end the call. But
 * `connected` flickers on any ordinary websocket reconnect, and failing instantly on that killed
 * healthy calls — releasing the microphone mid-sentence, with the alarm already consumed so there
 * was no way to re-dial.
 */
export const CHANNEL_GRACE_MS = 10_000

export interface CallHandles {
  mic: { current: MediaStream | null }
  push: { current: RTCPeerConnection | null }
  play: { current: RTCPeerConnection | null }
  abort: { current: AbortController | null }
  timers: { current: ReturnType<typeof setTimeout>[] }
  /**
   * Whether the call is still the operator's to act on.
   *
   * Cleared FIRST, before anything is torn down, so an async continuation that resolves midway
   * through a release cannot attach a peer to a call that is going away.
   */
  live: { current: boolean }
}

/**
 * Free everything a call holds. Deliberately says nothing about the phase — the caller owns that,
 * because "released" and "failed with a reason the operator can read" are different facts.
 */
export function releaseCall(h: CallHandles): void {
  h.live.current = false
  h.abort.current?.abort()
  h.abort.current = null
  for (const t of h.timers.current) clearTimeout(t)
  h.timers.current = []
  // Tracks BEFORE peers. Closing a peer alone leaves the operating system's microphone indicator
  // lit, which reads to an operator as a hot mic on a call that has already ended.
  h.mic.current?.getTracks().forEach(t => t.stop())
  h.mic.current = null
  h.push.current?.close()
  h.push.current = null
  h.play.current?.close()
  h.play.current = null
}
